/**
 * Il carico delle macchine — la fotografia in cima al Cruscotto.
 *
 * Sostituisce il KPI «capacità utilizzata», che era la **media delle linee
 * tagliata a 100 ciascuna**: sui dati veri diceva 94% mentre cinque linee su
 * dieci stavano fra il 136 e il 384 per cento. Cioè più il piano sfondava, più il
 * numero sembrava tranquillo — l'esatto contrario del suo mestiere.
 *
 * Qui ogni linea ha la sua barra, il **100% è un segno verticale** e i valori
 * oltre si vedono passare il segno. La scala arriva al 200%: una scala tarata
 * sul peggiore (384%) schiaccerebbe gli estrusori in un moncone illeggibile,
 * quindi oltre il 200 la barra arriva in fondo e il numero rosso dice quanto.
 */
import { cn } from "@/lib/utils";
import { CAP_UM_LABEL, num } from "@/pages/M2/format";
import { StatoLineaBadge } from "@/pages/M2/shared";
import type { LineaPianoRead } from "@/types/m2";

const SCALA = 200;

/** Come si calcola, in una frase: serve nel tooltip perché il numero sia
 *  controllabile da chi lo legge (e contestabile da Giacomo). */
const COME_SI_CALCOLA =
  "Quantità del piano diviso (capacità giornaliera × giorni in cui quella linea lavora). " +
  "Sopra il 100% il piano chiede alla macchina più di quanto sa fare in quei giorni.";

function Riga({ linea }: { linea: LineaPianoRead }) {
  const sat = linea.saturazione_pct;
  const oltre = sat !== null && sat > 100;
  const largh = sat === null ? 0 : Math.min(100, (sat / SCALA) * 100);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(60px,1.2fr)_46px] items-center gap-3 py-1">
      <div className="min-w-0">
        <div className="truncate text-[12.5px]">{linea.nome ?? linea.codice_macchina}</div>
        <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
          <StatoLineaBadge stato={linea.stato_rilevato} />
          {linea.capacita_valore !== null && linea.capacita_um && (
            <span>
              {num(linea.capacita_valore, linea.capacita_valore % 1 ? 1 : 0)} {CAP_UM_LABEL[linea.capacita_um]}
            </span>
          )}
        </div>
      </div>
      <div className="relative h-2 overflow-hidden rounded-sm bg-muted" title={COME_SI_CALCOLA}>
        <span
          className={cn(
            "block h-full rounded-sm",
            sat === null
              ? ""
              : oltre
                ? "bg-red-500"
                : sat >= 85
                  ? "bg-amber-500"
                  : "bg-brand-400"
          )}
          style={{ width: `${largh}%` }}
        />
        {/*
          Il 100%: senza un riferimento, «212%» non vuol dire niente.
          Va disegnato **sopra** la barra — sotto sparirebbe proprio sulle righe
          che lo superano, cioè le uniche in cui serve vederlo. Trovato
          guardando la pagina, non dai controlli.
        */}
        <span
          className="absolute inset-y-0 left-1/2 w-px bg-foreground/45 mix-blend-multiply"
          aria-hidden="true"
        />
      </div>
      <div
        className={cn(
          "text-right text-[12px] tabular-nums",
          oltre ? "font-semibold text-red-700 dark:text-red-400" : "text-muted-foreground"
        )}
      >
        {sat === null ? "—" : `${sat}%`}
      </div>
    </div>
  );
}

export function CaricoMacchine({ linee }: { linee: LineaPianoRead[] }) {
  const estr = linee.filter((l) => l.tipo === "estrusione");
  const conf = linee.filter((l) => l.tipo === "confezionamento");
  const nEstr = estr.reduce((a, l) => a + l.righe.length, 0);
  const nConf = conf.reduce((a, l) => a + l.righe.length, 0);

  return (
    <section>
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Come stanno le macchine
      </h2>
      <div className="grid rounded-lg border bg-card lg:grid-cols-2">
        <div className="p-3">
          <div className="mb-1.5 text-[11px] text-muted-foreground">
            Estrusione — {nEstr} lavorazioni
          </div>
          {estr.map((l) => (
            <Riga key={l.codice_macchina} linea={l} />
          ))}
        </div>
        <div className="border-t p-3 lg:border-l lg:border-t-0">
          <div className="mb-1.5 text-[11px] text-muted-foreground">
            Confezionamento — {nConf} lavorazioni
          </div>
          {conf.map((l) => (
            <Riga key={l.codice_macchina} linea={l} />
          ))}
        </div>
      </div>
    </section>
  );
}
